import { UserService } from 'src/app/_services/user.service';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { GlobalService } from 'src/app/_services/global.service';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';
import { AfterViewChecked, AfterViewInit, Component, DoCheck, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { usTimeZoneList } from 'src/app/_data/time-zones';
import { Router } from '@angular/router';
import { map } from 'rxjs';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { BusinessSetupProgressService } from 'src/app/_services/business-setup-progress.service';

@Component({
  selector: 'app-theme-overview',
  templateUrl: './theme-overview.component.html',
  styleUrls: ['./theme-overview.component.scss']
})
export class ThemeOverviewComponent implements OnInit, AfterViewInit {
  _global = GlobalService;
  @ViewChild('iframe') iframeElement!: ElementRef<HTMLIFrameElement>;;
  isLoaded: boolean = true;
  isStoreActionsLoading: boolean = true;
  isStoreEditorLoading: boolean = false;
  isStoreEditorReconnecting: boolean = false;
  webUrl: SafeResourceUrl;
  showMobileEditorDialog: boolean = false;
  showEditStorePopup: boolean = false;
  isEditStorePopupActionInProgress: boolean = false;
  editStorePopupActiveAction: 'submit' | 'self' | null = null;
  private readonly editStorePopupShownPrefix = 'edit-store-popup-shown';
  private readonly editStorePopupDontShowPrefix = 'edit-store-popup-dont-show';
  private readonly storeContentStatusOverridePrefix = 'dashboard-v2-store-content-status-override';
  private readonly storeContentStatusDoing = 'doing';
  // Keep this strict: if hostnames are included here, a stale/cached host can cause other users
  // to be mis-classified as a "Youks" user (and incorrectly show `younksnc.com`).
  private readonly youksStoreEmailAllowList = new Set([
    'zilonwebmaster@gmail.com',
    'zilonwebsmaster@gmail.com',
    'younksnutritioncentral@yahoo.com'
  ]);
  private readonly youksOnlyStoreHosts = ['younksnc.com', 'younks'];
  private readonly webStoreHostCachePrefix = 'webstore-host-cache';
  private readonly businessDomainOverrideEmails = new Set([
    'zilonwebmaster@gmail.com'
  ]);

  constructor(
    public _abd: AppBaseDataService,
    public _businessSetup: BusinessSetupService,
    public _userService: UserService,
    private _router: Router,
    private _connMessageService: ConnMessageService,
    private sanitizer: DomSanitizer,
    private _businessSetupProgress: BusinessSetupProgressService
  ) { }

  ngAfterViewInit() {
    // const iframeWindow = this.iframeElement.nativeElement.contentWindow;
    // if (iframeWindow) {
    //   iframeWindow.postMessage('Hello iframe', 'https://development.lincsell.com');
    // }
  }

  getUrl(url: any) {
    const normalizedUrl = this.ensureAbsoluteUrl(url);
    if (!normalizedUrl) {
      return 'URL not set';
    }

    try {
      const parsed = new URL(normalizedUrl);
      return `${parsed.host}${parsed.pathname === '/' ? '' : parsed.pathname}`;
    } catch {
      return String(url || '').replace(/^https?:\/\//i, '').replace(/\/+$/, '');
    }
  }

  get overviewDescription(): string {
    if (this.isYouksStoreUser()) {
      return 'Easily update your online store with our user-friendly editor. Please note, once you select a template, it cannot be changed later, so choose carefully.';
    }

    return 'Easily update your online store with our user-friendly editor.';
  }

  get shouldShowEditStoreButton(): boolean {
    return this.checkIsURL() && !this.isYouksStoreUser();
  }

  /** Domain/template setup only applies to merchants selling online — same signal as
   *  business-setup-checklist.component.ts's domainStepNeeded/shippingStepNeeded. */
  get sellsOnline(): boolean {
    const setup = this._global?.userInfo?.businessSetup?.[0];
    return !!(setup?.standardOnlineStore ?? setup?.StandardOnlineStore);
  }

  openPreview() {
    if (Array.isArray(this._global.webStoreConfiguration)) {
      let iswebStoreSelectedDefault: boolean = false
      iswebStoreSelectedDefault = this._global.webStoreConfiguration.filter(v => v.isDefault === true)?.length > 0 ? true : false
      if (this._global.webStoreConfiguration.length > 0 && !iswebStoreSelectedDefault) {
        this._router.navigate(["select-template"])
      }
    } else if (typeof this._global.webStoreConfiguration === 'object' && this._global.webStoreConfiguration !== null) {
      const hostUrl = this.ensureAbsoluteUrl(this.getResolvedWebStoreHost());
      if (hostUrl) {
        window.open(hostUrl + '/preview/?page_key=home', '_blank');
      }
    }
  }
  goToBusinessSetup(): void {
    // Use router state, not queryParams — a query string on this protected /app/* route hangs
    // the app (reproduces even on unrelated routes/params, unrelated to this feature specifically).
    this._router.navigate(['/app/business-setup'], { state: { section: 'domain' } });
  }

  goToSite() {
    if (!this.canAccessStoreWhileSetupIncomplete()) {
      return;
    }

    const hostUrl = this.ensureAbsoluteUrl(this.getResolvedWebStoreHost());
    if (hostUrl) {
      window.open(hostUrl, '_blank');
    }
  }
  goToEditer() {
    if (!this.canAccessStoreWhileSetupIncomplete()) {
      return;
    }

    if (this.isEditorBlockedOnMobile()) {
      this.showMobileEditorDialog = true;
      return;
    }
    this.openEditorDirectly();
  }

  private canAccessStoreWhileSetupIncomplete(): boolean {
    if (this.isSelfSetupOwnerFlow()) {
      return true;
    }

    if (this.isStoreContentPendingByClient()) {
      this._connMessageService.showToastMessage(
        `You're almost ready to edit your online store. Please upload your store content file first from Dashboard to continue.`,
        'warn'
      );
      return false;
    }

    if (this.isStoreContentInProgressByAdmin()) {
      this._connMessageService.showToastMessage(
        `Your online store is being set up. Your store link will be available once setup is complete.`,
        'warn'
      );
      return false;
    }

    return true;
  }

  onEditStorePopupSubmitStoreDetails(): void {
    this.handleEditStorePopupAction('submit');
  }

  onEditStorePopupDoItMyself(): void {
    this.handleEditStorePopupAction('self');
  }

  private handleEditStorePopupAction(action: 'submit' | 'self'): void {
    this.isEditStorePopupActionInProgress = true;
    this.editStorePopupActiveAction = action;
    this.showEditStorePopup = false;
    this.executeEditStorePopupAction(action);
  }

  private executeEditStorePopupAction(action: 'submit' | 'self'): void {
    if (action === 'submit') {
      this.applySubmitStoreDetailsFlags();
      sessionStorage.setItem('auto-login', '1');
      sessionStorage.removeItem('allow-store-setup-offer');
      sessionStorage.setItem('allow-store-setup-v2', '1');
      this._businessSetup.saveAvailSaveOffer({ availSaveOffer: true }).subscribe({
        next: () => {
          this.applySubmitStoreDetailsFlags();
          this._router.navigateByUrl(this._abd.getDashboardRoute());
        },
        error: () => {
          this._router.navigateByUrl(this._abd.getDashboardRoute());
        },
        complete: () => {
          this.resetEditStorePopupActionState();
        }
      });
      return;
    }

    this.applyDoItMyselfFlags();
    this._businessSetup.saveSkipedProductOnboardingOfferDisplayedOnOnlineStorePage({
      SkipedProductOnboardingOfferDisplayedOnOnlineStorePage: true
    }).subscribe({
      next: () => {
        this.applyDoItMyselfFlags();
        this.openEditorDirectly();
      },
      error: () => {
        this.openEditorDirectly();
        this.resetEditStorePopupActionState();
      },
      complete: () => {
        this.resetEditStorePopupActionState();
      }
    });
  }

  private resetEditStorePopupActionState(): void {
    this.isEditStorePopupActionInProgress = false;
    this.editStorePopupActiveAction = null;
  }

  /** "Let us set up your Online Store for Free" popup — superseded by the welcome-setup-modal
   *  + business-setup checklist on the dashboard, so this no longer shows for anyone. */
  private shouldShowEditStorePopup(): boolean {
    return false;
  }

  private isSelfSetupOwnerFlow(): boolean {
    const setup = this._global?.userInfo?.businessSetup?.[0];
    const availSaveOffer = this.toBooleanFlag(setup?.availSaveOffer ?? setup?.AvailSaveOffer);
    return availSaveOffer === false;
  }

  private markEditStorePopupShown(): void {
    try {
      localStorage.setItem(this.getEditStorePopupShownKey(), '1');
    } catch {
      // Ignore storage errors.
    }
  }

  private applyEditStorePopupDontShowPreference(): void {
    try {
      localStorage.setItem(this.getEditStorePopupDontShowKey(), '1');
    } catch {
      // Ignore storage errors.
    }

    if (this._global?.userInfo?.businessSetup?.[0]) {
      this._global.userInfo.businessSetup[0].dontShowEditStorePopup = true;
      this._global.userInfo.businessSetup[0].DontShowEditStorePopup = true;
    }
  }

  private applySubmitStoreDetailsFlags(): void {
    if (this._global.userInfo?.businessSetup?.[0]) {
      this._global.userInfo.businessSetup[0].availSaveOffer = true;
      this._global.userInfo.businessSetup[0].AvailSaveOffer = true;
      this._global.userInfo.businessSetup[0].storeContentStatus = 0;
      this._global.userInfo.businessSetup[0].StoreContentStatus = 0;
      this._global.userInfo.businessSetup[0].storeContentShowNotificationBar = true;
      this._global.userInfo.businessSetup[0].StoreContentShowNotificationBar = true;
    }

    this.updateBusinessInfoSession({
      availSaveOffer: true,
      AvailSaveOffer: true,
      storeContentStatus: 0,
      StoreContentStatus: 0,
      storeContentShowNotificationBar: true,
      StoreContentShowNotificationBar: true
    });
    this.persistStoreContentStatusOverride('pendingbyclient');
  }

  private applyDoItMyselfFlags(): void {
    if (this._global.userInfo?.businessSetup?.[0]) {
      this._global.userInfo.businessSetup[0].SkipedProductOnboardingOfferDisplayedOnOnlineStorePage = true;
      this._global.userInfo.businessSetup[0].skipedProductOnboardingOfferDisplayedOnOnlineStorePage = true;
    }

    this.updateBusinessInfoSession({
      SkipedProductOnboardingOfferDisplayedOnOnlineStorePage: true,
      skipedProductOnboardingOfferDisplayedOnOnlineStorePage: true
    });
    this.applyEditStorePopupDontShowPreference();
  }

  private updateBusinessInfoSession(updates: any): void {
    const rawBusinessInfo = sessionStorage.getItem('business-Info');
    if (!rawBusinessInfo) {
      return;
    }

    try {
      const businessInfo = JSON.parse(rawBusinessInfo);
      sessionStorage.setItem('business-Info', JSON.stringify({ ...businessInfo, ...updates }));
    } catch {
      // Ignore malformed session payloads.
    }
  }

  private getStoreContentStatusOverrideKey(): string {
    const setup = this._global?.userInfo?.businessSetup?.[0] || {};
    const businessId = String(setup?.businessId ?? setup?.id ?? 'default-business');
    const userId = String(this._global?.userInfo?.userID ?? this._global?.userInfo?.userId ?? 'default-user');
    return `${this.storeContentStatusOverridePrefix}-${userId}-${businessId}`;
  }

  private persistStoreContentStatusOverride(status: string): void {
    try {
      localStorage.setItem(this.getStoreContentStatusOverrideKey(), status);
    } catch {
      // Ignore localStorage errors.
    }
  }

  private getEditStorePopupShownKey(): string {
    const setup = this._global?.userInfo?.businessSetup?.[0] || {};
    const businessId = String(setup?.businessId ?? setup?.id ?? 'default-business');
    const userId = String(this._global?.userInfo?.userID ?? this._global?.userInfo?.userId ?? 'default-user');
    return `${this.editStorePopupShownPrefix}-${userId}-${businessId}`;
  }

  private getEditStorePopupDontShowKey(): string {
    const setup = this._global?.userInfo?.businessSetup?.[0] || {};
    const businessId = String(setup?.businessId ?? setup?.id ?? 'default-business');
    const userId = String(this._global?.userInfo?.userID ?? this._global?.userInfo?.userId ?? 'default-user');
    return `${this.editStorePopupDontShowPrefix}-${userId}-${businessId}`;
  }

  private toBooleanFlag(value: any): boolean {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1';
    }

    return value === true || value === 1;
  }

  private isStoreContentPendingByClient(): boolean {
    const setup = this._global?.userInfo?.businessSetup?.[0] || this.getBusinessInfoFromSession();
    const hasSubmissionEvidence = this.hasStoreContentSubmissionEvidence(setup);
    const overrideStatus = this.readStoreContentStatusOverride();
    if (overrideStatus) {
      return overrideStatus === 'pendingbyclient' || (overrideStatus === 'inprogress' && !hasSubmissionEvidence);
    }

    const normalizedStatus = this.normalizeStoreContentStatus(
      setup?.storeContentStatus
      ?? setup?.StoreContentStatus
      ?? setup?.storecontentStatus
      ?? setup?.storeContentstatus
      ?? setup?.storecontentstatus
      ?? setup?.status
      ?? setup?.Status
    );

    return normalizedStatus === 'pendingbyclient' || (normalizedStatus === 'inprogress' && !hasSubmissionEvidence);
  }

  private isStoreContentInProgressByAdmin(): boolean {
    const setup = this._global?.userInfo?.businessSetup?.[0] || this.getBusinessInfoFromSession();
    const hasSubmissionEvidence = this.hasStoreContentSubmissionEvidence(setup);
    if (!hasSubmissionEvidence) {
      return false;
    }

    const overrideStatus = this.readStoreContentStatusOverride();
    if (overrideStatus) {
      return overrideStatus === 'inprogress';
    }

    const normalizedStatus = this.normalizeStoreContentStatus(
      setup?.storeContentStatus
      ?? setup?.StoreContentStatus
      ?? setup?.storecontentStatus
      ?? setup?.storeContentstatus
      ?? setup?.storecontentstatus
      ?? setup?.status
      ?? setup?.Status
    );

    return normalizedStatus === 'inprogress';
  }

  private hasStoreContentSubmissionEvidence(setup?: any): boolean {
    const resolvedSetup = setup || this._global?.userInfo?.businessSetup?.[0] || this.getBusinessInfoFromSession() || {};
    const storeContentFileUrl = String(
      resolvedSetup?.storeContentFileUrl
      ?? resolvedSetup?.StoreContentFileUrl
      ?? resolvedSetup?.storecontentFileUrl
      ?? ''
    ).trim();
    const stripeOnboardingUrl = String(
      resolvedSetup?.stripeOnboardingUrl
      ?? resolvedSetup?.StripeOnboardingUrl
      ?? ''
    ).trim();
    const stripeOnboardingStatus = String(
      resolvedSetup?.stripeOnboardingStatus
      ?? resolvedSetup?.StripeOnboardingStatus
      ?? ''
    ).trim();

    return !!storeContentFileUrl || !!stripeOnboardingUrl || !!stripeOnboardingStatus;
  }

  private readStoreContentStatusOverride(): string {
    try {
      const raw = String(localStorage.getItem(this.getStoreContentStatusOverrideKey()) || '');
      return this.normalizeStoreContentStatus(raw);
    } catch {
      return '';
    }
  }

  private normalizeStoreContentStatus(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const normalized = String(value).trim().toLowerCase();
    if (
      normalized === '1'
      || normalized === 'pending by client'
      || normalized === 'pendingbyclient'
      || normalized === 'pending_by_client'
      || normalized === 'pending-by-client'
    ) {
      return 'pendingbyclient';
    }
    if (
      normalized === '0'
      || normalized === 'in progress'
      || normalized === 'inprogress'
      || normalized === this.storeContentStatusDoing
    ) {
      return 'inprogress';
    }
    if (normalized === '2' || normalized === 'completed') {
      return 'completed';
    }
    if (normalized === '3' || normalized === 'other') {
      return 'other';
    }

    return normalized;
  }

  private getBusinessInfoFromSession(): any {
    try {
      return JSON.parse(sessionStorage.getItem('business-Info') || 'null');
    } catch {
      return null;
    }
  }

  private safeParseJson(raw: string | null): any {
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private openEditorDirectly(): void {
    if (Array.isArray(this._global.webStoreConfiguration)) {
      let iswebStoreSelectedDefault: boolean = false
      iswebStoreSelectedDefault = this._global.webStoreConfiguration.filter(v => v.isDefault === true)?.length > 0 ? true : false
      if (this._global.webStoreConfiguration.length > 0 && !iswebStoreSelectedDefault) {
        this._router.navigate(["select-template"])
      }
    } else if (typeof this._global.webStoreConfiguration === 'object' && this._global.webStoreConfiguration !== null) {

      if (this.isStoreEditorFeatureEnabled()) {
        this.openStoreEditor();
      } else {
        window.open(this._global.webStoreConfiguration.demoUrl, '_blank')
      }
    }
  }

  private isStoreEditorFeatureEnabled(): boolean {
    // AI Store Editor is live for all accounts.
    return true;
  }

  get isStoreEditorTabOpen(): boolean {
    return !!this._global.storeEditorTabRef && !this._global.storeEditorTabRef.closed;
  }

  // Stores hosted on WordPress aren't provisioned in the AI editor backend — calling
  // b2c-exchange for them 500s, so open the store's own URL directly instead.
  // Editor selection is a single global flag from GET ai-editor/active-editor (authservice's
  // "CurrentEditor" app setting), resolved via getActiveEditorCached() below — of(cached) once
  // GlobalService.activeEditor is warm, otherwise a real fetch — rather than reading
  // GlobalService.activeEditor synchronously, which raced loadActiveEditor()'s own ngOnInit fetch
  // and could silently fall back to WordPress before it resolved.
  private loadActiveEditor(): void {
    this._businessSetup.getActiveEditorCached().subscribe();
  }

  private openStoreEditor(): void {
    // We can't read a cross-origin tab's URL to tell whether editor.lincsell.com's own session
    // has expired underneath it (e.g. the "Session expired" login screen), so instead of trusting
    // isStoreEditorTabOpen and just focusing a possibly-stale tab, every click re-runs the auth
    // exchange and re-navigates the existing tab (or opens a new one) with a fresh code/token —
    // a no-op if the old session was still good, and transparently logs back in if it wasn't.
    const reuseTab = this.isStoreEditorTabOpen ? this._global.storeEditorTabRef : null;
    reuseTab?.focus();

    // Open the tab synchronously (within the click handler) so browsers don't block it as a
    // popup once the async editor-flag/auth-code resolution completes — of(cached) still resolves
    // synchronously here in the common case (flag already warm from ngOnInit).
    this.isStoreEditorReconnecting = !reuseTab && !!this._global.storeEditorTabRef;
    this._global.storeEditorTabRef = null;
    const editorTab = reuseTab || window.open('', '_blank');

    this._businessSetup.getActiveEditorCached().subscribe((editor) => {
      if (!String(editor).trim().toLowerCase().includes('ai')) {
        this.isStoreEditorReconnecting = false;
        const wpUrl = this.ensureAbsoluteUrl(this.getResolvedWebStoreHost());
        if (!wpUrl) {
          editorTab?.close();
          return;
        }

        const target = `${wpUrl}?auth_token=signmein`;
        if (editorTab) {
          editorTab.location.href = target;
          this._global.storeEditorTabRef = editorTab;
        } else {
          this._global.storeEditorTabRef = window.open(target, '_blank');
        }
        return;
      }

      this.isStoreEditorLoading = true;
      const username = this.getCurrentUserEmails()[0] || '';
      const businessIdentifier = this._global?.userInfo?.businessSetup?.[0]?.businessNameWithDomain || '';
      this._businessSetup.exchangeStoreEditorAuthCode(username, businessIdentifier).subscribe((res: any) => {
        this.isStoreEditorLoading = false;
        this.isStoreEditorReconnecting = false;
        const code = res?.data?.code;
        if (res?.success === false || !code) {
          editorTab?.close();
          this._connMessageService.showToastMessage('Unable to open the online store editor. Please try again.', 'warn');
          return;
        }

        const callbackUrl = `${environment.StoreEditorBaseURL}auth/callback?code=${encodeURIComponent(code)}`;
        if (editorTab) {
          editorTab.location.href = callbackUrl;
          this._global.storeEditorTabRef = editorTab;
        } else {
          this._global.storeEditorTabRef = window.open(callbackUrl, '_blank');
        }
      });
    });
  }

  ngOnInit(): void {
    this.loadWebStoreConfiguration();
    this.loadActiveEditor();
  }

  checkIsURL() {
    // getResolvedWebStoreHost() can return an auto-provisioned default host that isn't actually
    // live yet — only treat the store as having a real URL once the merchant has explicitly saved
    // a domain/template on the business-setup checklist. isDomainTemplateSaved is the canonical
    // check (onlineStoreCreated backend field, falling back to the localStorage flag for older
    // accounts) — hasSavedDomainTemplate alone was localStorage-only and went stale across a
    // fresh login or a different browser/device, showing "Set up your domain & template" even
    // for accounts that already had one.
    if (!this._businessSetupProgress.isDomainTemplateSaved) {
      return false;
    }
    return !!this.ensureAbsoluteUrl(this.getResolvedWebStoreHost());
  }

  private isEditorBlockedOnMobile(): boolean {
    return window.innerWidth < 1024;
  }

  private ensureAbsoluteUrl(url: any): string {
    const raw = String(url || '').trim();
    if (!raw) {
      return '';
    }

    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }

    return `https://${raw}`;
  }

  private getResolvedWebStoreHost(): string {
    const overriddenHost = this.getOverriddenWebStoreHostForCurrentUser();
    if (overriddenHost) {
      return overriddenHost;
    }

    const businessSetup = this._global?.userInfo?.businessSetup?.[0] || {};
    const businessInfo = this.getBusinessInfoFromSession() || {};
    const rawWebStoreConfiguration = this._global?.webStoreConfiguration;
    const selectedWebStoreConfiguration = Array.isArray(rawWebStoreConfiguration)
      ? (rawWebStoreConfiguration.find((item: any) => item?.isDefault === true) ?? rawWebStoreConfiguration[0])
      : rawWebStoreConfiguration;

    const candidates = [
      // onlineStoreUrl is the field the checklist's "Domain & store template" step actually
      // writes on save (see business-setup-checklist.component.ts) and can be a fully custom
      // domain (e.g. "https://younksnc.com"), not just a lincsell.store subdomain — check it
      // first since it's the most authoritative, freshest source of truth.
      businessSetup?.onlineStoreUrl,
      businessSetup?.OnlineStoreUrl,
      selectedWebStoreConfiguration?.webStoreHost,
      selectedWebStoreConfiguration?.WebStoreHost,
      businessSetup?.webStoreHost,
      businessSetup?.WebStoreHost,
      businessSetup?.businessNameWithDomain,
      businessInfo?.webStoreHost,
      businessInfo?.WebStoreHost,
      businessInfo?.businessNameWithDomain
    ];

    const rawHostFromCandidates = candidates
      .map((value) => String(value || '').trim())
      .find((value) => value.length > 0 && value.toLowerCase() !== 'null' && value.toLowerCase() !== 'undefined');

    const rawHost = rawHostFromCandidates || this.readCachedWebStoreHost();

    if (!rawHost) {
      return '';
    }

    const normalizedHost = rawHost
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .replace(/\/+$/, '')
      .toLowerCase();
    const isYouksOnlyHost = this.youksOnlyStoreHosts.some((host) => normalizedHost.includes(host));

    if (isYouksOnlyHost && !this.isYouksStoreUser()) {
      // Prevent a stale/cached youks-only host from sticking around for other users.
      this.clearCachedWebStoreHost();
      return '';
    }

    if (rawHostFromCandidates) {
      this.writeCachedWebStoreHost(rawHostFromCandidates);
    }

    if (/^https?:\/\//i.test(rawHost)) {
      return rawHost;
    }

    if (normalizedHost.includes('.') || normalizedHost.includes('localhost')) {
      return normalizedHost;
    }

    return `${normalizedHost}.lincsell.com`;
  }

  private getOverriddenWebStoreHostForCurrentUser(): string {
    if (!this.shouldOverrideWebStoreHostForCurrentUser()) {
      return '';
    }

    const businessSetup = this._global?.userInfo?.businessSetup?.[0] || {};
    const businessInfo = this.getBusinessInfoFromSession() || {};
    const rawName = (
      businessSetup?.businessDisplayName
      ?? businessSetup?.businessName
      ?? businessInfo?.businessDisplayName
      ?? businessInfo?.businessName
      ?? ''
    );

    const slug = this.toSubdomainSlug(rawName);
    if (!slug) {
      return '';
    }

    const subdomain = slug.endsWith('lincsell') ? slug : `${slug}lincsell`;
    return `${subdomain}.lincsell.com`;
  }

  private shouldOverrideWebStoreHostForCurrentUser(): boolean {
    const normalizedAllowList = Array.from(this.businessDomainOverrideEmails)
      .map((value) => this.normalizeIdentifier(value))
      .filter((value) => value.length > 0);

    return this.getCurrentUserEmails().some((value) =>
      normalizedAllowList.includes(this.normalizeIdentifier(value))
    );
  }

  private toSubdomainSlug(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  private getWebStoreHostCacheKey(): string {
    const setup = this._global?.userInfo?.businessSetup?.[0] || {};
    const businessId = String(setup?.businessId ?? setup?.id ?? 'default-business');
    const userId = String(this._global?.userInfo?.userID ?? this._global?.userInfo?.userId ?? 'default-user');
    return `${this.webStoreHostCachePrefix}-${userId}-${businessId}`;
  }

  private readCachedWebStoreHost(): string {
    try {
      return String(localStorage.getItem(this.getWebStoreHostCacheKey()) || '').trim();
    } catch {
      return '';
    }
  }

  private writeCachedWebStoreHost(host: string): void {
    const normalized = String(host || '').trim();
    if (!normalized || normalized.toLowerCase() === 'null' || normalized.toLowerCase() === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(this.getWebStoreHostCacheKey(), normalized);
    } catch {
      // Ignore storage errors.
    }
  }

  private clearCachedWebStoreHost(): void {
    try {
      localStorage.removeItem(this.getWebStoreHostCacheKey());
    } catch {
      // Ignore storage errors.
    }
  }
 
  private isYouksStoreUser(): boolean {
    const allowList = new Set(
      Array.from(this.youksStoreEmailAllowList)
        .map((value) => this.normalizeIdentifier(value))
        .filter((value) => value.length > 0)
    );

    return this.getCurrentUserEmails().some((value) =>
      allowList.has(this.normalizeIdentifier(value))
    );
  }

  private getCurrentUserEmails(): string[] {
    const businessInfo = this.getBusinessInfoFromSession();
    const localUser = this.safeParseJson(localStorage.getItem('user'));
    const loginUser = this.safeParseJson(sessionStorage.getItem('login-user'));
    const currentUser = this._userService.currentUserObj as any;

    return [
      this._global?.userInfo?.email,
      currentUser?.email,
      localUser?.email,
      loginUser?.email,
      businessInfo?.email,
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter((value) => value.length > 0);
  }

  private normalizeIdentifier(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .replace(/\/+$/, '')
      .replace(/[^a-z0-9@.]+/g, '');
  }

  private loadWebStoreConfiguration(): void {
    if (this._global?.whiteLabel?.wlName !== 'LincSell') {
      this.isStoreActionsLoading = false;
      return;
    }

    const previousConfig = this._global.webStoreConfiguration;

    this._userService.getWebStoreConfiguration().subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) {
          this._global.webStoreConfiguration = res.data;
        } else if (!previousConfig) {
          this._global.webStoreConfiguration = null;
        }
      },
      error: () => {
        // Keep last known config if refresh fails (prevents URL flicker / reset on navigation).
        if (!previousConfig) {
          this._global.webStoreConfiguration = null;
        }
        this.isStoreActionsLoading = false;
      },
      complete: () => {
        this.isStoreActionsLoading = false;
      }
    });
  }

}
